<template>
    <v-card-text class="pt-0 px-0">
        <div v-if="hasSavedPhotos" class="d-flex align-center mb-2" style="gap: 8px">
            <v-chip color="success" outlined small>
                <v-icon left small>mdi-check</v-icon>
                {{ value.length }} photo(s) sauvegardée(s)
            </v-chip>
            <v-btn icon x-small color="primary" @click="previewDialog = true">
                <v-icon small>mdi-eye</v-icon>
            </v-btn>
        </div>

        <v-file-input
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            :rules="effectiveRules"
            v-model="image"
            :placeholder="hasSavedPhotos ? 'Changer la photo' : placeholder"
            multiple
            accept="image/*"
            required
            filled
            hide-details="auto"
            @change="displayImagesPreview(image)"
        >
            <template #append>
                <v-icon @click.stop="$refs.cameraInput.click()">mdi-camera</v-icon>
            </template>
        </v-file-input>

        <!-- Input natif caché pour la caméra directe -->
        <input ref="cameraInput" type="file" accept="image/*" capture="environment" hidden @change="onCameraSelected" />

        <v-dialog v-model="previewDialog" max-width="600">
            <v-card>
                <v-card-title class="d-flex justify-space-between align-center">
                    <span>Photos sauvegardées</span>
                    <v-btn icon @click="previewDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col v-for="(src, i) in value" :key="i" cols="12" sm="6">
                            <v-img :src="src" contain max-height="300" class="rounded"/>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-card-text>
</template>

<script>
import imageCompression from "browser-image-compression";

export default {
    name: "AppImageInput",
    data() {
        return {
            image: null,
            previewDialog: false,
        };
    },
    props: {
        value: {
            default: null,
        },
        placeholder: {
            type: String,
            required: true,
        },
        rules: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    computed: {
        hasSavedPhotos() {
            return Array.isArray(this.value) && this.value.length > 0 && !this.image
        },
        effectiveRules() {
            if (this.hasSavedPhotos) return this.rules.map(() => () => true)
            return this.rules
        },
    },
    methods: {
        // Resizes the photo via the native createImageBitmap API (resize during decoding,
        // without ever materializing the image at full resolution in memory). If the API
        // doesn't exist or fails for any reason, the original file is returned as-is: the
        // rest of the pipeline (imageCompression) then retries with the original file,
        // exactly as before this fix.
        async preResizeIfPossible(file, maxDim) {
            if (typeof createImageBitmap !== "function") return file;
            let bitmap;
            try {
                bitmap = await createImageBitmap(file, { resizeWidth: maxDim, resizeQuality: "medium" });

                // Safety net: if resizeWidth was ignored by the browser, we still force
                // drawing into a small canvas (the destination canvas stays small even
                // if the source bitmap is large).
                const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
                const targetWidth = Math.max(1, Math.round(bitmap.width * scale));
                const targetHeight = Math.max(1, Math.round(bitmap.height * scale));

                const canvas = document.createElement("canvas");
                canvas.width = targetWidth;
                canvas.height = targetHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);

                const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
                if (!blob) return file;

                return new File([blob], file.name, {
                    type: "image/jpeg",
                    lastModified: new Date().getTime(),
                });
            } catch (error) {
                console.error("AppImageInput: pre-resize failed, retrying with the original file", error);
                return file;
            } finally {
                bitmap?.close?.();
            }
        },
        async onCameraSelected(event) {
            const files = Array.from(event.target.files);
            if (!files.length) return;
            this.image = files;
            await this.displayImagesPreview(files);
            event.target.value = '';
        },
        async displayImagesPreview(imgs) {
            if (imgs === null) {
                this.$emit("input", []);
                return;
            }

            const toBase64 = (file) =>
                new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = (error) => reject(error);
                });

            const options = {
                maxSizeMB: 0.1,
                maxWidthOrHeight: 1080,
                useWebWorker: true,
            };

            this.$store.dispatch("photoCompression/start");
            try {
                const res = [];
                for (const img of imgs) {
                    // Recent smartphone photos can be 12 to 48+ MP (tens of MB once decoded in
                    // memory) even when the JPEG file itself is only a few MB. The
                    // "browser-image-compression" lib first decodes the image at its native
                    // resolution into a <canvas> before downscaling it: on some mobile browsers
                    // (iOS Safari's canvas/memory limits in particular, or low-end Android),
                    // this full-resolution decode fails even though the exact same file works
                    // fine on desktop. We therefore pre-shrink the photo via createImageBitmap
                    // (native, memory-efficient resize) before handing it to the lib, which
                    // then only has a small image left to process.
                    const safeImg = await this.preResizeIfPossible(img, 1080);
                    const compressedFile = await imageCompression(safeImg, options);
                    const file = new File([compressedFile], compressedFile.name, {
                        type: compressedFile.type,
                        lastModified: new Date().getTime(),
                    });
                    res.push(await toBase64(file));
                }
                this.$emit("input", res);
            } catch (error) {
                // E.g. a "Motion Photo"/hybrid file, or a file too heavy for the compression
                // step to decode. We only reset the current selection (not the already-
                // emitted "value"): if a valid photo already existed (restored draft), it is
                // preserved and shown again as "saved"; otherwise the field goes back to
                // empty and the "required" rule becomes invalid again (before this fix, the
                // form could be validated with a "selected" photo that was never actually
                // saved).
                this.image = null;
                console.error("AppImageInput: failed to compress a photo", error);
                this.$store.dispatch("snackbar/show", {
                    snackbar_text: "La photo n'a pas pu être traitée (format ou fichier non supporté). Merci de reprendre la photo.",
                    snackbar_type: "error",
                });
            } finally {
                this.$store.dispatch("photoCompression/end");
            }
        },
    },
};
</script>
